import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faImage,
  faSpinner,
  faWandMagicSparkles,
  faEye,
  // Added for remove button logic
} from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';

const CoverImagePreview = ({
  stagedFile, // Nuevo archivo seleccionado por el usuario (File object)
  existingImageUrl, // URL privada firmada de la imagen guardada
  stylizedImageUrl, // URL pública estilizada de la imagen guardada
  temporaryPreviewUrl, // URL de blob temporal (de preview de efecto)
  isUserView, // Booleano: ¿está activa la vista de usuario?
  coverFileEffect, // Objeto { type, intensity } del efecto actual
  isPremium, // Booleano: ¿el contenido es premium?
  isPreviewing, // Booleano: ¿se está generando una preview de efecto?
  disabled = false, // Booleano: ¿están deshabilitadas las acciones?
  label, // Texto para el label
  description, // Texto descriptivo
  maxFileSizeMB = 10, // Límite de tamaño en MB
  onFileSelect, // Callback al seleccionar un archivo nuevo
  onFileRemove, // Callback al quitar la imagen (nueva o existente)
  onEditEffect, // Callback al hacer clic en editar efecto
}) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false); // Para feedback visual de drag-n-drop
  const fileInputRef = useRef(null); // Referencia al input[type=file] oculto
  const [stagedBlobUrl, setStagedBlobUrl] = useState(null); // URL de blob para el stagedFile

  // --- Efecto para crear/revocar URL de blob SÓLO para stagedFile ---
  useEffect(() => {
    let objectUrl = null;
    // Si hay un stagedFile y es un Blob/File válido...
    if (stagedFile instanceof Blob) {
      try {
        objectUrl = URL.createObjectURL(stagedFile); // ...crea una URL local
        setStagedBlobUrl(objectUrl);
      } catch (error) {
        console.error('Error creating object URL for staged cover:', error);
        setStagedBlobUrl(null); // Falla si no se puede crear
      }
    } else {
      setStagedBlobUrl(null); // Limpia si no hay stagedFile
    }
    // Función de limpieza: revoca la URL cuando el stagedFile cambia o el componente se desmonta
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [stagedFile]); // Solo depende de stagedFile

  // --- Lógica para determinar la URL FINAL a mostrar ---
  const getFinalImageUrl = () => {
    // Prioridad 1: Mostrar el archivo nuevo (staged) si existe
    if (stagedBlobUrl) {
      return stagedBlobUrl;
    }
    // Prioridad 2: Mostrar el blob de preview de efecto si existe
    if (temporaryPreviewUrl) {
      return temporaryPreviewUrl;
    }
    // Prioridad 3: Si es Vista de Usuario, mostrar la pública estilizada
    if (isUserView) {
      return stylizedImageUrl; // Puede ser null si no existe
    }
    // Prioridad 4: Si es Vista de Owner, mostrar la privada firmada
    return existingImageUrl; // Puede ser null si no existe
  };

  const finalImageUrl = getFinalImageUrl(); // Calcula la URL en cada render

  // --- Lógica de Handlers (sin cambios funcionales, solo limpieza) ---
  const handleFileChange = (e) => {
    if (disabled) return;
    const file = e.target.files?.[0]; // Usar optional chaining
    if (file) {
      if (file.size > maxFileSizeMB * 1024 * 1024) {
        toast.error(t('file_upload.error_size', { maxFileSizeMB }));
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error(t('file_upload.error_type_image'));
        return;
      }
      if (typeof onFileSelect === 'function') onFileSelect(file);
    }
    // Limpiar el input para permitir seleccionar el mismo archivo de nuevo
    if (e.target) e.target.value = '';
  };

  const handleRemove = () => {
    if (disabled) return;
    if (typeof onFileRemove === 'function') onFileRemove();
    // No es necesario limpiar fileInputRef.current.value aquí si handleFileChange lo hace
  };

  const handleDragEvents = (e, dragging) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setIsDragging(dragging);
  };

  const handleDrop = (e) => {
    if (disabled) return;
    handleDragEvents(e, false); // Quitar estado dragging
    const file = e.dataTransfer.files?.[0]; // Usar optional chaining
    // Simular evento para reutilizar handleFileChange
    if (file) handleFileChange({ target: { files: [file] } });
  };

  // Determina si se puede editar el efecto
  const canEditEffect =
    isPremium &&
    typeof onEditEffect === 'function' &&
    (stagedFile || existingImageUrl); // Si hay archivo nuevo O uno existente

  // --- Renderizado ---
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-neutral-800 dark:text-neutral-200">
        {label || t('coverImagePreview.defaultLabel', 'Imagen de Portada')}
      </label>
      {description && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          {description}
        </p>
      )}
      {/* Input oculto */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        accept="image/png, image/jpeg, image/webp" // Tipos aceptados
        disabled={disabled}
      />

      {/* Contenedor principal: Muestra imagen o placeholder */}
      {finalImageUrl ? (
        // --- Si hay imagen para mostrar ---
        <div className="relative group w-full aspect-video rounded-lg overflow-hidden border border-neutral-300 dark:border-neutral-700 bg-neutral-100 dark:bg-neutral-800">
          <img
            src={finalImageUrl} // Usa la URL calculada
            alt={t('file_upload.preview_alt', 'Vista previa')}
            className="w-full h-full object-cover transition-opacity duration-300"
            style={{ opacity: isPreviewing ? 0.5 : 1 }} // Atenúa si está cargando preview
            // Fallback por si la URL falla
            onError={(e) => {
              e.target.src = '/images/default-post-image-error.png';
            }}
          />
          {/* Spinner si se está generando preview de efecto */}
          {isPreviewing && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                className="text-white text-3xl"
              />
            </div>
          )}

          {/* Chip de efecto (Solo en Vista Owner y si hay efecto) */}
          {coverFileEffect &&
            coverFileEffect.type !== 'none' &&
            !isUserView && (
              <div className="absolute bottom-1.5 left-1.5 bg-primary text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-lg flex items-center gap-1 z-10 pointer-events-none">
                <FontAwesomeIcon icon={faWandMagicSparkles} size="xs" />
                <span className="capitalize">
                  {coverFileEffect.type.replace('_', ' ')}:{' '}
                  {coverFileEffect.intensity}
                </span>
              </div>
            )}

          {/* Overlay de Vista Usuario (Solo si está activa) */}
          {isUserView && (
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex flex-col items-center justify-center text-white p-2 text-center z-20 pointer-events-none">
              <FontAwesomeIcon icon={faEye} className="text-xl" />
            </div>
          )}

          {/* Botones de acción (Solo en Vista Owner) */}
          {!isUserView && (
            <div className="absolute inset-0 bg-black/40 lg:bg-black/60 opacity-100 lg:opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
              {/* Botón Editar Efecto */}
              {canEditEffect && (
                <button
                  type="button"
                  onClick={onEditEffect} // Llama al handler del hook
                  className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center bg-black/40 hover:bg-black/60 text-white rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={disabled}
                  aria-label={t('file_upload.edit_effect')}
                >
                  <FontAwesomeIcon icon={faWandMagicSparkles} size="sm" />{' '}
                  {/* Ajuste tamaño icono */}
                </button>
              )}
              {/* Botones Cambiar / Quitar */}
              <div className="absolute bottom-2 right-2 flex gap-2">
                <button
                  type="button"
                  // Abre el selector de archivos al hacer clic
                  onClick={() => !disabled && fileInputRef.current?.click()}
                  className="btn-secondary !text-xs !py-1 !px-2.5" // Ajuste padding/texto
                  disabled={disabled}
                >
                  {t('common.change', 'Cambiar')}
                </button>
                <button
                  type="button"
                  onClick={handleRemove} // Llama al handler de quitar
                  className="btn-secondary !bg-red-500/10 !text-red-600 hover:!bg-red-500/20 !text-xs !py-1 !px-2.5" // Ajuste padding/texto y colores
                  disabled={disabled}
                >
                  {/* Reemplazar icono por texto para claridad */}
                  {/* <FontAwesomeIcon icon={faTimes} /> */}
                  {t('common.remove', 'Quitar')}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        // --- Si NO hay imagen para mostrar (Placeholder) ---
        <div
          onClick={() => !disabled && fileInputRef.current?.click()} // Abre selector al hacer clic
          // Handlers para Drag & Drop
          onDragEnter={(e) => handleDragEvents(e, true)}
          onDragLeave={(e) => handleDragEvents(e, false)}
          onDragOver={(e) => e.preventDefault()} // Necesario para que onDrop funcione
          onDrop={handleDrop}
          // Estilos condicionales (deshabilitado, arrastrando)
          className={`w-full aspect-video rounded-lg p-6 flex flex-col items-center justify-center text-center border-2 border-dashed transition-colors ${
            disabled
              ? 'cursor-not-allowed bg-neutral-50 dark:bg-neutral-800/50 opacity-60' // Estilo deshabilitado
              : 'cursor-pointer'
          } ${
            isDragging
              ? 'border-primary bg-primary/10' // Estilo al arrastrar sobre él
              : 'border-neutral-300 dark:border-neutral-600 hover:border-primary/50' // Estilo normal
          }`}
        >
          {/* Contenido del Placeholder */}
          <FontAwesomeIcon
            icon={faImage}
            className="text-3xl text-neutral-400 dark:text-neutral-500 mb-2"
          />
          <span className="text-sm font-semibold text-neutral-600 dark:text-neutral-300">
            {t('file_upload.select_file', 'Seleccionar o arrastrar imagen')}
          </span>
          <span className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
            {t('file_upload.drag_drop_text_image', { maxFileSizeMB })}{' '}
            {/* Texto específico para imagen */}
          </span>
        </div>
      )}
    </div>
  );
};

export default CoverImagePreview;
